import { graphqlPost } from "api/graphqlClient";
import restClient from "api/restClient";
import { Button } from "components/Button/Button";
import { BufferedDialogButtons, useBufferedDialogForm } from "components/BufferedDialogForm";
import { AutocompleteOption } from "components/Form/components/Autocomplete/Autocomplete";
import { SimpleSelect } from "components/Form/components/Autocomplete/components/SimpleSelect";
import { IconCollection } from "components/Icon/IconCollection";
import PageContent from "components/Layout/PageContent";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import { useMigrateOnSaveParams } from "containers/Resources/screens/ResourcesSingle/useCanCommitOnSave";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { getSearchObj } from "utils/utils";
import { containerSettingsBus } from "./containerSettingsBus";
import "./ContainerDashboardManager.scss";

interface DashboardRef {
    id: string;
    name: string;
}

/**
 * One row of the configured-dashboards draft.
 *
 * Existing associations carry the join-subrecord `id`; a row the user has added
 * in the current (unsaved) edit session has no `id` yet — it is materialised
 * into a `container_dashboard` record only on Save. `dashboard_id` is unique
 * across the whole list (a dashboard can be associated at most once), so it
 * doubles as each row's stable React key / removal handle.
 */
interface DraftAssoc {
    id?: string;
    record_id: string;
    dashboard_id: string;
    is_default: boolean;
    dashboard_id_dashboard?: DashboardRef | null;
}

interface DashboardOption {
    id: string;
    name: string;
}

/** Structural key for a draft row, for dirty-detection against the baseline. */
const draftKey = (row: DraftAssoc) =>
    `${row.id ?? "new"}|${row.dashboard_id}|${row.is_default ? 1 : 0}`;

/** Two drafts are equal when they hold the same rows in the same default-state. */
const draftsEqual = (a: DraftAssoc[], b: DraftAssoc[]): boolean => {
    if (a.length !== b.length) {
        return false;
    }
    const sa = a.map(draftKey).sort();
    const sb = b.map(draftKey).sort();
    return sa.every((value, index) => value === sb[index]);
};

/**
 * Container-screen management panel for configurable dashboards (slice 2b).
 *
 * Lets a user with write access on the container (WORKSPACE / PROJECT / BUNDLE)
 * add / remove / set-default the DASHBOARD records associated with it via the
 * `container_dashboard` join subrecord. The associated dashboards render as a
 * tab bar elsewhere on the screen ({@link ContainerDashboardTabs}); this panel
 * is the editor for that list.
 *
 * Editing is BUFFERED: add / remove / set-default all mutate a local draft, and
 * the draft is reconciled to the server (create / delete / update mutations)
 * only when the user clicks **Save**. Cancel/Close discards the draft. The
 * shared {@link useBufferedDialogForm} primitive owns baseline↔draft sync and
 * dirty detection; {@link BufferedDialogButtons} renders the Save/Cancel footer.
 *
 * Reads and writes go through raw GraphQL (`graphqlPost` / `restClient.post`)
 * because the committed `schema.graphql` SDL does not yet include the
 * `container_dashboard` type — the deployed backend does. This mirrors the
 * read-side tab bar and MetadataList/CommentsPanel.
 *
 * `is_default` is single-per-container, enforced here at the UI layer: setting a
 * new default clears any previously-default row in the draft. The list includes
 * a built-in "Default dashboard" entry (the container's usual Usage & activity +
 * resource-tabs view); it is the default whenever no additional dashboard is
 * marked default, and can be re-selected as default to clear a custom default.
 * A newly-added dashboard is never made default automatically.
 */
export const ContainerDashboardManager = ({
    recordId,
    isDisabled,
    variant,
    description,
    onClose,
}: {
    recordId?: string;
    isDisabled?: boolean;
    /** "dialog" drops the standalone page chrome for embedding in a settings modal. */
    variant?: "dialog";
    /** Optional descriptive text rendered directly under the "Dashboards" heading. */
    description?: ReactNode;
    /** In a dialog: called by Cancel (after discarding the draft) to dismiss. */
    onClose?: () => void;
}) => {
    const form = useBufferedDialogForm<DraftAssoc[]>(draftsEqual);
    const draft = form.draft;

    const [dashboards, setDashboards] = useState<DashboardOption[] | null>(null);
    const [isAdding, setIsAdding] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const migrateOnSaveParams = useMigrateOnSaveParams();
    const params = useMemo(() => ({ commit: false, ...migrateOnSaveParams }), [migrateOnSaveParams]);

    const { load } = form;
    const fetchAssocs = useCallback(() => {
        if (!recordId) {
            return Promise.resolve();
        }
        const filter = JSON.stringify(
            getSearchObj({ field: "record_id", op: "eq", value: recordId })
        );
        const query = `query ContainerDashboardsManage($filter: String) {
            container_dashboard_plain_list(filter: $filter, order: "+id") {
                id
                record_id
                dashboard_id
                is_default
                dashboard_id_dashboard {
                    id
                    name
                }
            }
        }`;
        return graphqlPost({ query, variables: { filter } })
            .then((response) => {
                load((response?.data?.container_dashboard_plain_list ?? []) as DraftAssoc[]);
            })
            .catch(() => load([]));
    }, [recordId, load]);

    useEffect(() => {
        fetchAssocs();
    }, [fetchAssocs]);

    // The full dashboard list feeds the add picker; fetched once per screen.
    useEffect(() => {
        let cancelled = false;
        const query = `query DashboardPickerList($order: String) {
            dashboard_plain_list(order: $order) {
                id
                name
            }
        }`;
        graphqlPost({ query, variables: { order: "+name" } })
            .then((response) => {
                if (!cancelled) {
                    setDashboards(response?.data?.dashboard_plain_list ?? []);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setDashboards([]);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Dashboards not already in the draft, as picker options.
    const pickerOptions: AutocompleteOption[] = useMemo(() => {
        const associated = new Set((draft ?? []).map((row) => row.dashboard_id));
        return (dashboards ?? [])
            .filter((dashboard) => !associated.has(dashboard.id))
            .map((dashboard) => ({ value: dashboard.id, label: dashboard.name }));
    }, [draft, dashboards]);

    // --- Buffered edits: mutate the local draft only (no persistence). -------

    const { setDraft } = form;

    const addDashboard = useCallback(
        (dashboardId: string, dashboardName: string) => {
            setIsAdding(false);
            // A newly-added dashboard is never made default automatically — the
            // built-in "Default dashboard" stays the default until the user
            // explicitly picks another one (see the "Set as default" affordance).
            setDraft((current) => [
                ...(current ?? []),
                {
                    record_id: recordId ?? "",
                    dashboard_id: dashboardId,
                    is_default: false,
                    dashboard_id_dashboard: { id: dashboardId, name: dashboardName },
                },
            ]);
        },
        [setDraft, recordId]
    );

    const removeDashboard = useCallback(
        (dashboardId: string) => {
            setDraft((current) => (current ?? []).filter((row) => row.dashboard_id !== dashboardId));
        },
        [setDraft]
    );

    const setDefault = useCallback(
        (dashboardId: string) => {
            setDraft((current) =>
                (current ?? []).map((row) => ({
                    ...row,
                    is_default: row.dashboard_id === dashboardId,
                }))
            );
        },
        [setDraft]
    );

    // Make the built-in "Default dashboard" the container default by clearing
    // is_default from every additional dashboard (default = no assoc is default).
    const setBuiltinDefault = useCallback(() => {
        setDraft((current) =>
            (current ?? []).map((row) => (row.is_default ? { ...row, is_default: false } : row))
        );
    }, [setDraft]);

    // --- Persistence: reconcile the draft into mutations, fired on Save. -----

    const createAssoc = useCallback(
        (row: DraftAssoc) =>
            restClient.post(
                "/graphql",
                {
                    query: `mutation create_container_dashboard($record_id: String!, $dashboard_id: String!, $is_default: Boolean) {
                        create_container_dashboard(body: {record_id: $record_id, dashboard_id: $dashboard_id, is_default: $is_default}) {
                            id
                        }
                    }`,
                    variables: {
                        record_id: recordId,
                        dashboard_id: row.dashboard_id,
                        is_default: row.is_default,
                    },
                },
                { params }
            ),
        [recordId, params]
    );

    const deleteAssoc = useCallback(
        (assocId: string) =>
            restClient.post(
                "/graphql",
                {
                    query: `mutation delete_container_dashboard($id: String!) {
                        delete_container_dashboard(id: $id)
                    }`,
                    variables: { id: assocId },
                },
                { params }
            ),
        [params]
    );

    const updateIsDefault = useCallback(
        (row: DraftAssoc, isDefault: boolean) =>
            restClient.post(
                "/graphql",
                {
                    query: `mutation update_container_dashboard($record_id: String!, $dashboard_id: String!, $is_default: Boolean, $id: String!) {
                        update_container_dashboard(body: {record_id: $record_id, dashboard_id: $dashboard_id, is_default: $is_default}, id: $id) {
                            id
                        }
                    }`,
                    variables: {
                        record_id: row.record_id,
                        dashboard_id: row.dashboard_id,
                        is_default: isDefault,
                        id: row.id,
                    },
                },
                { params }
            ),
        [params]
    );

    const saveChanges = useCallback(() => {
        const baseRows = form.baseline ?? [];
        const draftRows = form.draft ?? [];
        const baselineById = new Map(
            baseRows.filter((row) => row.id).map((row) => [row.id as string, row])
        );
        const draftIds = new Set(draftRows.filter((row) => row.id).map((row) => row.id as string));

        const deletes: string[] = [];
        baselineById.forEach((_row, id) => {
            if (!draftIds.has(id)) {
                deletes.push(id);
            }
        });

        const creates = draftRows.filter((row) => !row.id);
        const clears: DraftAssoc[] = [];
        const sets: DraftAssoc[] = [];
        draftRows.forEach((row) => {
            if (!row.id) {
                return;
            }
            const base = baselineById.get(row.id);
            if (base && base.is_default !== row.is_default) {
                (row.is_default ? sets : clears).push(row);
            }
        });

        if (!deletes.length && !creates.length && !clears.length && !sets.length) {
            return;
        }

        setIsSaving(true);
        // Order: clear old defaults, delete removed rows, create new rows (with
        // their drafted default), then set the new default among existing rows —
        // preserving the single-default-per-container invariant at every step.
        const tasks: Array<() => Promise<unknown>> = [
            ...clears.map((row) => () => updateIsDefault(row, false)),
            ...deletes.map((id) => () => deleteAssoc(id)),
            ...creates.map((row) => () => createAssoc(row)),
            ...sets.map((row) => () => updateIsDefault(row, true)),
        ];

        tasks
            .reduce((chain, task) => chain.then(task), Promise.resolve() as Promise<unknown>)
            .then(() => fetchAssocs())
            .then(() => {
                // Let the read-side tab strip re-query immediately (no page refresh).
                if (recordId) {
                    containerSettingsBus.notifyChanged(recordId);
                }
            })
            .catch((error) =>
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride="Sorry, couldn't save the dashboard changes"
                        error={error}
                    />
                )
            )
            .finally(() => setIsSaving(false));
    }, [form.baseline, form.draft, updateIsDefault, deleteAssoc, createAssoc, fetchAssocs, recordId]);

    const handleCancel = useCallback(() => {
        setIsAdding(false);
        form.reset();
        onClose?.();
    }, [form, onClose]);

    const hasCustomDefault = (draft ?? []).some((row) => row.is_default);

    if (!recordId) {
        return null;
    }

    const canEdit = !isDisabled;

    // react-select props forwarded to SimpleSelect via a spread (its typed props
    // cover only optionList/selectedValue; it forwards the rest to react-select,
    // exactly as the shared Autocomplete wrapper does).
    const pickerSelectProps = {
        placeholder: "Select a dashboard to add…",
        isSearchable: true,
        isDisabled: isSaving,
        autoFocus: true,
        onChange: (option: AutocompleteOption | null) => {
            if (option) {
                addDashboard(option.value, option.label);
            }
        },
    };

    const content = (
        <>
            <div className="subrecordHeader">
                <h3>Dashboards</h3>
                {canEdit && !isAdding && (
                    <div className="subrecordHeaderActions">
                        <Button
                            size="small"
                            icon={IconCollection.plus}
                            label="Add"
                            variation="dark"
                            disabled={isSaving || pickerOptions.length === 0}
                            title={
                                pickerOptions.length === 0
                                    ? "All dashboards are already added"
                                    : "Add a dashboard"
                            }
                            onClick={() => setIsAdding(true)}
                        />
                    </div>
                )}
            </div>

            {description && (
                <div className="containerDashboardManager__description">{description}</div>
            )}

            {canEdit && isAdding && (
                <div className="containerDashboardManager__picker">
                    <div className="containerDashboardManager__pickerSelect">
                        <SimpleSelect
                            optionList={pickerOptions}
                            selectedValue={null}
                            {...pickerSelectProps}
                        />
                    </div>
                    <Button
                        label="Cancel"
                        variation="white"
                        size="small"
                        onClick={() => setIsAdding(false)}
                    />
                </div>
            )}

            {draft === null ? null : draft.length === 0 ? (
                <div className="subrecord-empty-list">
                    No dashboards are added yet. The built-in default
                    dashboards are always shown; dashboards added here appear as extra tabs.
                </div>
            ) : (
                // Rendered as an aligned table (not per-row flex) so the Name
                // column consumes the available width instead of truncating early,
                // column widths stay uniform across rows, and the Default indicator
                // sits in a fixed column position regardless of which row is default.
                <table className="containerDashboardManager__table">
                    <colgroup>
                        <col className="containerDashboardManager__colName" />
                        <col className="containerDashboardManager__colDefault" />
                        {canEdit && <col className="containerDashboardManager__colActions" />}
                    </colgroup>
                    <tbody>
                        <tr className="containerDashboardManager__row">
                            <td className="containerDashboardManager__cellName">
                                <span
                                    className="containerDashboardManager__name"
                                    title="The built-in view (Usage & activity + resource tabs)"
                                >
                                    Default dashboard
                                </span>
                            </td>
                            <td className="containerDashboardManager__cellDefault">
                                {!hasCustomDefault ? (
                                    <span className="containerDashboardManager__defaultBadge">
                                        Default
                                    </span>
                                ) : (
                                    canEdit && (
                                        <Button
                                            label="Set as default"
                                            variation="white"
                                            size="small"
                                            disabled={isSaving}
                                            onClick={setBuiltinDefault}
                                        />
                                    )
                                )}
                            </td>
                            {canEdit && (
                                <td className="containerDashboardManager__cellActions" />
                            )}
                        </tr>
                        {draft.map((assoc) => (
                            <tr
                                key={assoc.dashboard_id}
                                className="containerDashboardManager__row"
                            >
                                <td className="containerDashboardManager__cellName">
                                    <span
                                        className="containerDashboardManager__name"
                                        title={
                                            assoc.dashboard_id_dashboard?.name ??
                                            assoc.dashboard_id
                                        }
                                    >
                                        {assoc.dashboard_id_dashboard?.name ?? assoc.dashboard_id}
                                    </span>
                                </td>
                                <td className="containerDashboardManager__cellDefault">
                                    {assoc.is_default ? (
                                        <span className="containerDashboardManager__defaultBadge">
                                            Default
                                        </span>
                                    ) : (
                                        canEdit && (
                                            <Button
                                                label="Set as default"
                                                variation="white"
                                                size="small"
                                                disabled={isSaving}
                                                onClick={() => setDefault(assoc.dashboard_id)}
                                            />
                                        )
                                    )}
                                </td>
                                {canEdit && (
                                    <td className="containerDashboardManager__cellActions">
                                        <Button
                                            icon={IconCollection.deleteOutline}
                                            variation="icon"
                                            ariaLabel={`Remove ${
                                                assoc.dashboard_id_dashboard?.name ?? "dashboard"
                                            }`}
                                            title="Remove"
                                            disabled={isSaving}
                                            onClick={() => removeDashboard(assoc.dashboard_id)}
                                        />
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {canEdit && draft !== null && (
                <BufferedDialogButtons
                    onCancel={handleCancel}
                    onSave={saveChanges}
                    isDirty={form.isDirty}
                    isSaving={isSaving}
                    cancelLabel={onClose ? "Cancel" : "Discard changes"}
                    saveLabel="Save"
                />
            )}
        </>
    );

    if (variant === "dialog") {
        return <div className="containerDashboardManager containerDashboardManager--dialog">{content}</div>;
    }

    return (
        <PageContent
            key="containerDashboardsManage"
            className="subrecordList containerDashboardManager"
        >
            {content}
        </PageContent>
    );
};

export default ContainerDashboardManager;
